import React from 'react';
import { Paper, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getButtonColor } from '../../theme/themeUtils';

export interface ContentCardProps {
  children: React.ReactNode;
  /** Optional DOM id (e.g. for in-page anchors / deep links) */
  id?: string;
  /** Optional left accent border color (e.g. success for "Only for Me", warning for auction) */
  leftBorderColor?: string | null;
  /** Optional hover border color (default: theme accent) */
  hoverBorderColor?: string;
  /** Optional click handler (e.g. for selectable cards) */
  onClick?: () => void;
  sx?: object;
}

export const ContentCard = React.forwardRef<HTMLDivElement, ContentCardProps>(function ContentCard({
  children,
  id,
  leftBorderColor,
  hoverBorderColor,
  onClick,
  sx = {},
}, ref) {
  const theme = useTheme();
  const accent = getButtonColor(theme);
  const hoverColor = hoverBorderColor ?? alpha(accent, 0.3);

  return (
    <Paper
      id={id}
      ref={ref}
      elevation={0}
      onClick={onClick}
      sx={{
        p: 0,
        borderRadius: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        ...(leftBorderColor && { borderLeft: `4px solid ${leftBorderColor}` }),
        overflow: 'hidden',
        transition: 'all 0.15s ease',
        // Touch-friendly: avoid accidental taps on adjacent cards
        '@media (hover: hover)': {
          '&:hover': {
            boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.12)}`,
            borderColor: hoverColor,
          },
        },
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
});
